const IDLE_MS = 30 * 60 * 1000;
const MAX_QUEUE = 80;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function createActivityTracker({ storage, send, uuid, now = Date.now, visible = () => true, schedule = setTimeout, cancel = clearTimeout }) {
  let queue = [];
  let timer = null;
  let inFlight = false;
  let failures = 0;
  let userId = null;
  let generation = 0;
  let memorySession = null;
  const recent = new Map();

  function reset() {
    queue = []; recent.clear(); failures = 0; userId = null; memorySession = null; generation++;
    if (timer) cancel(timer);
    timer = null;
  }
  function arm(delay = 1200) {
    if (!timer) timer = schedule(() => { timer = null; void flush(); }, delay);
  }
  async function flush() {
    if (inFlight || !queue.length) return;
    const batch = queue.slice(0, 20);
    const ownerGeneration = generation;
    const batchOwner = userId;
    inFlight = true;
    try {
      await send(batch, batchOwner);
      if (ownerGeneration === generation) { queue.splice(0, batch.length); failures = 0; }
    } catch {
      if (ownerGeneration === generation) failures++;
    } finally {
      inFlight = false;
      if (queue.length && failures < 4) arm(Math.min(30000, 1500 * (2 ** failures)));
    }
  }
  function track(owner, type, module, metadata = {}) {
    if (!owner || !visible()) return;
    try {
      if (owner !== userId) { reset(); userId = owner; }
      const currentTime = now();
      const key = `nume_activity_v1:${owner}`;
      let session;
      try { session = JSON.parse(storage.getItem(key) || 'null'); } catch { /* unavailable storage */ }
      session ||= memorySession;
      const newSession = !UUID.test(session?.id || '') || !Number.isFinite(session.touched) || currentTime - session.touched >= IDLE_MS || session.touched > currentTime;
      session = { id: newSession ? uuid() : session.id, touched: currentTime };
      memorySession = session;
      try { storage.setItem(key, JSON.stringify(session)); } catch { /* telemetry never blocks UI */ }
      // Dedupe mount/StrictMode/retry events without counting rendering as use.
      const dedupe = `${type}:${module}:${JSON.stringify(metadata)}`;
      if (!newSession && ['app_open', 'module_view'].includes(type) && currentTime - (recent.get(dedupe) ?? -Infinity) < 2000) return;
      recent.set(dedupe, currentTime);
      if (recent.size > 100) recent.delete(recent.keys().next().value);
      if (queue.length >= MAX_QUEUE - 1) return;
      if (newSession && type !== 'app_open') queue.push({ id: uuid(), sessionId: session.id, type: 'app_open', module: 'app', metadata: {} });
      queue.push({ id: uuid(), sessionId: session.id, type, module, metadata });
      failures = 0;
      arm();
    } catch { /* unsupported APIs must never affect the application */ }
  }
  return { track, flush, reset };
}

let tracker;
function getTracker() {
  if (tracker) return tracker;
  tracker = createActivityTracker({
    storage: { getItem: key => localStorage.getItem(key), setItem: (key, value) => localStorage.setItem(key, value) },
    uuid: () => crypto.randomUUID(),
    visible: () => document.visibilityState === 'visible',
    send: async (events, owner) => {
      const token = localStorage.getItem('auth_token');
      if (!token || tokenOwner(token)?.userId !== owner) throw new Error('Sesión diferente');
      const response = await fetch('/api/activity/record', { method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ events }), signal: AbortSignal.timeout(5000), keepalive: true });
      if (!response.ok) throw new Error('Seguimiento no disponible');
    }
  });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') void tracker.flush(); });
  window.addEventListener('pagehide', () => { void tracker.flush(); });
  return tracker;
}

function tokenOwner(token) {
  try { return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); }
  catch { return null; }
}

export function trackActivity(type, module, metadata = {}) {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    const data = tokenOwner(token);
    if (!UUID.test(data?.userId || '') || data.isDemo || data.mustChangePassword || data.exp * 1000 <= Date.now()) return;
    getTracker().track(data.userId, type, module, metadata);
  } catch { /* no logging of tokens or personal data */ }
}

export function resetActivity() { tracker?.reset(); }
